import { computed, observable, reaction, runInAction } from "mobx"

import { Model } from "@/lib/Model.ts"

import type { IComputedValue } from "mobx"

export class PaginationModel extends Model {
  readonly currentDot = observable.box(0)
  readonly totalDots = observable.box(1)

  private readonly scrollLeft = observable.box(0)
  private readonly scrollWidth = observable.box(0)
  private readonly clientWidth = observable.box(0)

  private element: HTMLElement | null = null

  constructor(
    readonly cards: IComputedValue<unknown[]>,
    readonly cardWidth: number = 320,
    readonly cardGap: number = 32,
    readonly padding: number = 88
  ) {
    super("PaginationModel")

    this.lifetime.add(
      reaction(
        () => cards.get().length,
        () => {
          this.calculateDots()
          this.updateCurrentDot()
        },
        { fireImmediately: true }
      ),
      "PaginationModel:projectCards"
    )
  }

  readonly hasLeftShadow = computed(() => {
    return this.scrollLeft.get() > 0
  })

  readonly hasRightShadow = computed(() => {
    const scrollLeft = this.scrollLeft.get()
    const scrollWidth = this.scrollWidth.get()
    const clientWidth = this.clientWidth.get()
    // The "+1" is a small tolerance to account for rounding errors and subpixel differences in scroll calculations
    return scrollLeft + clientWidth < scrollWidth - 1
  })

  readonly refCallback = (element: HTMLElement | null) => {
    if (!element || this.element === element) {
      return
    }

    this.element = element

    setTimeout(() => {
      this.calculateDots()
      this.updateCurrentDot()
      this.updateScrollState()
    }, 0)

    element.addEventListener("scroll", this.onScroll)
    window.addEventListener("resize", this.onResize)

    this.lifetime.add(() => {
      element.removeEventListener("scroll", this.onScroll)
      window.removeEventListener("resize", this.onResize)
      this.element = null
    }, "PaginationModel:refCallback")
  }

  private readonly onScroll = () => {
    this.updateCurrentDot()
    this.updateScrollState()
  }

  private readonly onResize = () => {
    this.calculateDots()
    this.updateCurrentDot()
    this.updateScrollState()
  }

  private readonly updateScrollState = () => {
    runInAction(() => {
      if (!this.element) return
      this.scrollLeft.set(this.element.scrollLeft)
      this.scrollWidth.set(this.element.scrollWidth)
      this.clientWidth.set(this.element.clientWidth)
    })
  }

  private readonly calculateDots = () => {
    if (!this.element) return

    const scrollContainer = this.element.querySelector("[data-scroll-container]")
    if (!scrollContainer) return

    const cardWidth = this.cardWidth + this.cardGap
    const viewportWidth = this.element.clientWidth - (this.padding * 2)
    const cardsPerViewport = Math.max(1, Math.floor(viewportWidth / cardWidth))
    const totalCards = this.cards.get().length + 1 // +1 for the "Add project" card
    const totalDots = Math.max(1, Math.ceil(totalCards / cardsPerViewport)) // TODO this grows indefinitely, limit

    runInAction(() => {
      this.totalDots.set(totalDots)
    })
  }

  private readonly updateCurrentDot = () => {
    if (!this.element) return

    const scrollContainer = this.element.querySelector("[data-scroll-container]")
    if (!scrollContainer) return

    const cardWidth = this.cardWidth + this.cardGap
    const viewportWidth = this.element.clientWidth - (this.padding * 2)
    const cardsPerViewport = Math.max(1, Math.floor(viewportWidth / cardWidth))
    const viewportSize = cardsPerViewport * cardWidth
    const currentDot = Math.floor((this.element.scrollLeft + viewportSize / 2) / viewportSize)

    runInAction(() => {
      const element = this.element

      if (element) {
        // Special handling of first and last dot in case currentDot calculation is off due to rounding
        if (element.scrollLeft === 0) {
          this.currentDot.set(0)
        } else if (element.scrollLeft + element.clientWidth === element.scrollWidth) {
          this.currentDot.set(this.totalDots.get() - 1)
        } else {
          this.currentDot.set(Math.min(Math.max(0, currentDot), this.totalDots.get() - 1))
        }
      }
    })
  }

  readonly navigateToDot = (dotIndex: number) => {
    if (!this.element) return

    const cardWidth = this.cardWidth + this.cardGap
    const viewportWidth = this.element.clientWidth - (this.padding * 2)
    const cardsPerViewport = Math.max(1, Math.floor(viewportWidth / cardWidth))
    const targetScrollLeft = dotIndex * cardsPerViewport * cardWidth

    this.element.scrollTo({
      left: targetScrollLeft,
      behavior: "smooth"
    })
  }
}
